import {UWM_MODULE_LIVE_TOUR_TARGET} from './consts';
import {sendMessageToParent} from './sendMessage';

const TARGET_ATTRIBUTE = 'data-live-tour-target';
const SCROLLABLE_OVERFLOWS = ['auto', 'scroll'];

let frameId = 0;

/** Keeps the host's live tour target in sight and reports its viewport box on change; `null` stops. */
export const showLiveTourTarget = (payload: Record<string, unknown>) => {
    const target = payload.target as string | null | undefined;

    cancelAnimationFrame(frameId);

    if (!target) return;

    let lastReport: string | undefined;

    const update = () => {
        frameId = requestAnimationFrame(update);

        const element = document.querySelector(`[${TARGET_ATTRIBUTE}="${CSS.escape(target)}"]`);
        const box = element ? toBox(element, keepInView(element)) : {rect: null};
        const report = JSON.stringify(box);

        if (report === lastReport) return;

        lastReport = report;
        sendMessageToParent({type: UWM_MODULE_LIVE_TOUR_TARGET, target, ...box});
    };

    update();
};

function toBox(element: Element, {x, y, width, height}: DOMRect) {
    if (!height) return {rect: null};

    return {rect: {x, y, width, height}, radius: Number.parseFloat(getComputedStyle(element).borderTopLeftRadius) || 0};
}

// Within the scroll container's box minus its scroll-padding (what the host covers).
function keepInView(element: Element) {
    const rect = element.getBoundingClientRect();
    const container = findScrollContainer(element);

    if (!container || !rect.height) return rect;

    const {top, bottom} = container.getBoundingClientRect();
    const {scrollPaddingTop, scrollPaddingBottom} = getComputedStyle(container);
    const areaTop = top + (Number.parseFloat(scrollPaddingTop) || 0);
    const areaBottom = bottom - (Number.parseFloat(scrollPaddingBottom) || 0);
    // A target taller than the area shows its top.
    const offset =
        rect.top < areaTop ? rect.top - areaTop : Math.max(Math.min(rect.bottom - areaBottom, rect.top - areaTop), 0);

    if (!offset) return rect;

    // Instant over the CSS smooth scroll: the box is read right after.
    container.scrollBy({top: offset, behavior: 'instant'});

    return element.getBoundingClientRect();
}

function findScrollContainer(element: Element) {
    for (let node = element.parentElement; node; node = node.parentElement) {
        if (SCROLLABLE_OVERFLOWS.includes(getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight) {
            return node;
        }
    }

    return null;
}
